import type { SupabaseClient } from "@supabase/supabase-js";
import type { Database } from "@/integrations/supabase/types";

type EnvMap = Record<string, string | boolean | undefined>;

const readEnv = (env: EnvMap, key: string) => {
  const value = env[key];
  return typeof value === "string" && value.trim() !== "" ? value.trim() : undefined;
};

const readNumberEnv = (env: EnvMap, key: string, fallback: number) => {
  const value = readEnv(env, key);
  if (!value) return fallback;
  const parsed = Number(value);
  return Number.isFinite(parsed) && parsed >= 0 ? parsed : fallback;
};

const env = import.meta.env;
const sentryDsn = readEnv(env, "VITE_SENTRY_DSN");
type SentryModule = typeof import("@sentry/browser");

let sentryPromise: Promise<SentryModule | null> | null = null;
let sentryInitialized = false;
const SAFE_CONTEXT_KEYS = new Set(["feature", "route", "code", "action", "surface", "provider", "status"]);

const sanitizeContextValue = (value: unknown): string | number | boolean | null => {
  if (typeof value === "string") return value.trim() || null;
  if (typeof value === "number" || typeof value === "boolean") return value;
  return null;
};

const sanitizeMonitoringContext = (context?: Record<string, unknown>) => {
  if (!context) return undefined;

  const sanitized = Object.entries(context).reduce<Record<string, string | number | boolean | null>>(
    (result, [key, value]) => {
      if (!SAFE_CONTEXT_KEYS.has(key)) return result;
      const sanitizedValue = sanitizeContextValue(value);
      if (sanitizedValue === null) return result;
      result[key] = sanitizedValue;
      return result;
    },
    {},
  );

  return Object.keys(sanitized).length > 0 ? sanitized : undefined;
};

export const isMonitoringEnabled = () => Boolean(sentryDsn);

const loadSentry = async () => {
  if (!sentryDsn || typeof window === "undefined") return null;
  sentryPromise ??= import("@sentry/browser");
  return sentryPromise;
};

export const initializeMonitoring = async () => {
  if (sentryInitialized) return;

  const Sentry = await loadSentry();
  if (!Sentry) return;

  Sentry.init({
    dsn: sentryDsn,
    environment: readEnv(env, "VITE_APP_ENV") ?? env.MODE,
    release: readEnv(env, "VITE_SENTRY_RELEASE"),
    tracesSampleRate: readNumberEnv(env, "VITE_SENTRY_TRACES_SAMPLE_RATE", 0),
    sendDefaultPii: false,
  });

  sentryInitialized = true;
};

export const bindMonitoringToSupabaseAuth = (supabase: SupabaseClient<Database>) => {
  if (!isMonitoringEnabled()) return;

  const setSessionUser = async (user: { id: string; email?: string } | null | undefined) => {
    const Sentry = await loadSentry();
    if (!Sentry) return;
    Sentry.setUser(user ? { id: user.id } : null);
  };

  void supabase.auth.getSession().then(({ data }) => {
    void setSessionUser(data.session?.user);
  });

  const {
    data: { subscription },
  } = supabase.auth.onAuthStateChange((_event, session) => {
    void setSessionUser(session?.user);
  });

  return () => subscription.unsubscribe();
};

export const captureAppException = (
  error: unknown,
  context?: Record<string, unknown>,
  onEventId?: (eventId: string) => void,
) => {
  if (!isMonitoringEnabled()) return;
  void loadSentry().then((Sentry) => {
    const eventId = Sentry?.captureException(error, { extra: sanitizeMonitoringContext(context) });
    if (eventId && onEventId) onEventId(eventId);
  });
};
